import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import type { AuthConfigResponse } from '@jabberr/core';
import { api, ApiError, errorMessage } from '../lib/api.js';
import { sendMagicLink, signInWithMicrosoft, signInWithPassword } from '../lib/auth.js';
import { useSession } from '../state/session.js';
import { ErrorBox } from '../components/ui.js';
import { safeLoginReturn } from '../lib/login-return.js';

/** When /api/auth-config cannot be read, assume production (email flow) so the page stays usable. */
const FALLBACK_CONFIG: AuthConfigResponse = { microsoft: false, magicLink: true, devShortcut: false, mailer: 'acs' };

/** The one-click dev button signs in as this seeded address; it must exist in SEED_USERS. */
const DEV_OWNER_EMAIL = 'john@tarawa.group';

/** Maps a failed GET /api/dev/magic-link to a message that says what actually happened. */
function devSignInError(err: unknown, email: string): string {
  if (err instanceof ApiError && err.status === 404) {
    if (err.code === 'user_not_found') return `No account for ${email}. Sign-up is invite-only; ask John to add you.`;
    // Only a route-level 404 (no user_not_found code) means the shortcut is not mounted.
    if (err.code === 'not_found') return 'The dev shortcut is off on the api; set DEV_LOGIN_SHORTCUT=1 and restart it.';
  }
  return `Dev sign-in failed: ${errorMessage(err)}`;
}

export function LoginPage() {
  const { user, loading } = useSession();
  const [params] = useSearchParams();
  const destination = safeLoginReturn(params.get('returnTo'));
  const callback = new URL(destination, window.location.origin).toString();
  const [email, setEmail] = useState('');
  const [config, setConfig] = useState<AuthConfigResponse | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The App Store and Google Play reviewer form stays folded away until someone asks for it.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewPassword, setReviewPassword] = useState('');

  useEffect(() => {
    api.authConfig()
      .then(setConfig)
      .catch((err: unknown) => {
        setError(`Could not load sign-in options: ${errorMessage(err)}`);
        setConfig(FALLBACK_CONFIG);
      });
  }, []);

  useEffect(() => {
    const err = params.get('error');
    if (err) setError(err === 'INVALID_TOKEN' || err === 'EXPIRED_TOKEN' ? 'That link is invalid or has expired. Request a new one.' : err);
  }, [params]);

  if (!loading && user) return <Navigate to={destination} replace />;

  const submitEmail = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const errorCallback = new URL(destination === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(destination)}`, window.location.origin).toString();
      await sendMagicLink(email.trim(), callback, errorCallback);
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitDev = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const address = email.trim();
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.devMagicLink(address);
      window.location.assign(url);
    } catch (err) {
      setError(devSignInError(err, address));
    } finally {
      setBusy(false);
    }
  };

  const submitReview = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithPassword(reviewEmail.trim(), reviewPassword);
      // A full page load, so the iOS and Android sign-in sheets see the signed-in page and close.
      window.location.assign('/');
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const reviewForm = config?.reviewLogin ? (
    reviewOpen ? (
      <form onSubmit={(e) => void submitReview(e)} className="stack review-login">
        <p className="muted small">For App Store and Google Play review. Use the account in the review notes.</p>
        <label htmlFor="review-email" className="field-label">Email</label>
        <input id="review-email" type="email" autoComplete="username" required value={reviewEmail} onChange={(e) => setReviewEmail(e.target.value)} disabled={busy} />
        <label htmlFor="review-password" className="field-label">Password</label>
        <input id="review-password" type="password" autoComplete="current-password" required value={reviewPassword} onChange={(e) => setReviewPassword(e.target.value)} disabled={busy} />
        <button type="submit" className="btn" disabled={busy || !reviewEmail || !reviewPassword}>
          Sign in with password
        </button>
      </form>
    ) : (
      <button type="button" className="login-review-toggle" onClick={() => { setError(null); setReviewOpen(true); }}>
        App reviewer? Sign in with email and password
      </button>
    )
  ) : null;

  const emailInput = (disabled: boolean) => (
    <>
      <label htmlFor="email" className="field-label">Work email</label>
      <input
        id="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@tarawagroup.com"
        disabled={disabled}
        autoFocus
      />
    </>
  );

  let body;
  if (!config) {
    body = <p className="muted">Loading sign-in options.</p>;
  } else if (!config.magicLink && !config.guestMagicLink) {
    body = config.microsoft || config.reviewLogin ? null : <p className="muted">Sign-in is not configured. Contact John.</p>;
  } else if (!config.magicLink) {
    // Staff email sign-in is off; a guest who accepted a project invitation may still get an email link.
    body = sent ? (
      <div className="notice">
        <strong>Check your email.</strong> If {email} accepted an invitation to a Jabberr project, a sign-in link is on its way.
      </div>
    ) : (
      <form onSubmit={(e) => void submitEmail(e)} className="stack">
        <p className="muted small">Invited guest? Sign in with the address your invitation went to.</p>
        {emailInput(false)}
        <button type="submit" className="btn btn-primary" disabled={busy || !email}>
          Email me a sign-in link
        </button>
      </form>
    );
  } else if (config.mailer === 'console') {
    // Development: nothing sends email, so the only way in is the dev shortcut.
    const off = !config.devShortcut;
    const devLogin = (): void => {
      // The bookmarkable dev link signs in and lands on the web app in one hop.
      window.location.assign(`/api/dev/login?email=${encodeURIComponent(DEV_OWNER_EMAIL)}`);
    };
    body = (
      <form onSubmit={(e) => void submitDev(e)} className="stack">
        {emailInput(off)}
        <button type="submit" className="btn btn-primary" disabled={off || busy || !email}>
          Sign in
        </button>
        {!off && (
          <button type="button" className="btn" onClick={devLogin}>
            Sign in as John
          </button>
        )}
        <p className="muted small">
          {off
            ? 'No mailer is configured and the dev shortcut is off; set DEV_LOGIN_SHORTCUT=1 on the api.'
            : 'Development mode: no email is sent. This signs you in directly if the address has been invited.'}
        </p>
      </form>
    );
  } else if (sent) {
    body = (
      <div className="notice">
        <strong>Check your email.</strong> We sent a sign-in link to {email}. Sign-up is invite-only; if nothing arrives, ask
        John to add you.
      </div>
    );
  } else {
    body = (
      <form onSubmit={(e) => void submitEmail(e)} className="stack">
        {emailInput(false)}
        <button type="submit" className="btn btn-primary" disabled={busy || !email}>
          Email me a sign-in link
        </button>
      </form>
    );
  }

  return (
    <div className="login">
      <div className="login-card">
        <span className="login-otter" aria-hidden="true" />
        <div className="wordmark big">Jabberr</div>
        <p className="muted">Talk to a product manager who understands the project.</p>
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        {body}
        {config?.microsoft ? (
          <>
            {config.magicLink || config.guestMagicLink ? <div className="divider">or</div> : null}
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => {
                setError(null);
                void signInWithMicrosoft(callback).catch((err: unknown) => setError(errorMessage(err)));
              }}
              aria-label="Sign in with Microsoft"
            >
              <svg width="16" height="16" viewBox="0 0 23 23" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" />
                <rect x="12" y="1" width="10" height="10" />
                <rect x="1" y="12" width="10" height="10" />
                <rect x="12" y="12" width="10" height="10" />
              </svg>
              Sign in with Microsoft
            </button>
          </>
        ) : null}
        {reviewForm ? <div className="login-review">{reviewForm}</div> : null}
      </div>
    </div>
  );
}
